import * as React from 'react'
import timelineHelper from '@helpers/timeline-helper'
import { Apartment } from '@models/apartment'
import { differenceInDays, endOfMonth, set } from 'date-fns'
import { RootState, useAppSelector } from '@store/index'
import { selectRentSessions } from '@store/selectors/timeline'
import { parseISODate } from '@helpers/date-helper'
import { CalendarDate, TimelineRentSession } from '@store/reducers/timeline-reducers'
import classNames from 'classnames'

interface Props {
  renderedDates: CalendarDate[]
  allApartments: Apartment[]
}

interface ApartmentWithSessions {
  apartment: Apartment
  sessions: TimelineRentSession[]
}

export const TimelineVirtualizeSessions = ({ renderedDates, allApartments }: Props): React.ReactNode => {
  const rentSessions = useAppSelector(selectRentSessions)
  const isCompactView = useAppSelector((state: RootState) => state.timelineState.isCompactView)

  const isSessionRendered = (session: TimelineRentSession): boolean => {
    const now = new Date()
    const firstRenderedDate = renderedDates[0]
    const lastRenderedDate = renderedDates[renderedDates.length - 1]

    const { start, end } = {
      start: set(now, { year: firstRenderedDate.year, month: firstRenderedDate.month - 1, date: 1 }),
      end: endOfMonth(set(now, { year: lastRenderedDate.year, month: lastRenderedDate.month - 1, date: 1 })),
    }

    const sessionDateFrom = parseISODate(session.date_from) as Date
    const sessionDateTo = parseISODate(session.date_to) as Date

    return sessionDateFrom <= end && sessionDateTo >= start
  }

  const apartmentsWithSessions = allApartments.reduce<ApartmentWithSessions[]>((prev, apartment) => {
    const rentSessionTagKeywords = (apartment.rent_session_tags || []).map(tag => tag.keyword)
    const hasSessionTag = rentSessionTagKeywords.length > 0

    if (!hasSessionTag) return prev

    return [
      ...prev,
      {
        apartment,
        sessions: rentSessions.filter((session: TimelineRentSession) => {
          if (!rentSessionTagKeywords.includes(session.keyword)) return false
          return isSessionRendered(session)
        }),
      },
    ]
  }, [])

  return apartmentsWithSessions.map(({ apartment, sessions }) =>
    sessions.map((session: TimelineRentSession) => (
      <div
        className={classNames('calendar-content__session', { 'is-compact': isCompactView })}
        key={session.id}
        style={{
          left: timelineHelper.getItemLeftOffset(renderedDates, session.date_from),
          width: timelineHelper.getItemWidth(
            differenceInDays(parseISODate(session.date_to) as Date, parseISODate(session.date_from) as Date),
          ),
          top: timelineHelper.getItemTopOffset(apartment, allApartments),
        }}
      />
    )),
  )
}
